import { fetchApi } from "@/api/client";
import type { PaginatedResponse, ThemeSummary } from "@/types/catalog";
import type { ThemeDetailResponse, ThemeListResponse } from "@/types/themes";

// CR-110: themes-by-category listing for the Generate-preview picker.
// page is 1-based to match backend; caller is responsible for paginating
// until total_pages is reached (see useCategoriesAndThemes).
export const API_THEMES_PATH = "/api/themes" as const;
export const API_THEMES_BY_SLUG_PATH = "/api/themes/by-slug" as const;

export async function listThemesByCategory(
  categoryId: string,
  page: number,
  signal?: AbortSignal,
): Promise<PaginatedResponse<ThemeSummary>> {
  const qs = new URLSearchParams({
    category_id: categoryId,
    status: "ACTIVE",
    page: String(page),
    page_size: "100",
  });
  return fetchApi<PaginatedResponse<ThemeSummary>>(
    `${API_THEMES_PATH}?${qs.toString()}`,
    { signal },
  );
}

// CR-111: admin-facing themes list wrapper. Returns the extended
// ThemeListItem shape (summary + preview fields from the LEFT-JOIN
// enrichment added in Phase 1). `category_id` and `status` are both
// optional so ThemesListPage can load the full catalog.
export interface ListThemesQuery {
  readonly page: number;
  readonly page_size: number;
  readonly category_id?: string;
  readonly status?: string;
}

export async function listThemes(
  query: ListThemesQuery,
  signal?: AbortSignal,
): Promise<ThemeListResponse> {
  const qs = new URLSearchParams({
    page: String(query.page),
    page_size: String(query.page_size),
  });
  if (query.category_id) qs.set("category_id", query.category_id);
  if (query.status) qs.set("status", query.status);
  return fetchApi<ThemeListResponse>(
    `${API_THEMES_PATH}?${qs.toString()}`,
    { signal },
  );
}

// CR-111: detail fetch by slug. The backend exposes a dedicated
// `/api/themes/by-slug/{slug}` endpoint to avoid clashing with the
// existing UUID-keyed `/api/themes/{theme_id}` route.
export async function getThemeBySlug(
  slug: string,
  signal?: AbortSignal,
): Promise<ThemeDetailResponse> {
  return fetchApi<ThemeDetailResponse>(
    `${API_THEMES_BY_SLUG_PATH}/${encodeURIComponent(slug)}`,
    { signal },
  );
}
